import React, { useState, useEffect } from "react";
import { useParams, useRouteMatch, useHistory, Link } from "react-router-dom";
import { Tabs, Spin, Button, Form } from "antd";
import { Main } from "../styled";
import { PageHeader } from "../../components/page-headers/page-headers";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import {
  useCarOwnerById,
  useOwnerCars,
  useOwnerProducts,
} from "./detailDataQueries";
import { useDispatch } from "react-redux";
import { setAllCars } from "../../redux/cars/actionCreator";
import { setTableTab2Filer } from "../../redux/car_owners/actionCreator";
import {
  setCarOwnerAllProducts,
  setTableFilterCarOwnerProducts,
} from "../../redux/products/actionCreator";
import { Tab1 } from "./overview/tabs/tab1";
import { Tab2 } from "./overview/tabs/tab2";
import { Tab3 } from "./overview/tabs/tab3";

const { TabPane } = Tabs;

const graphqlVariables = {
  tokenGet: process.env.REACT_APP_TOKEN_GET,
  tokenMutation: process.env.REACT_APP_TOKEN_MUTATION,
};

const OwnerDetailPage = ({ formReadonly = true, showEditButton = true }) => {
  const { t } = useTranslation("common");
  const { ownerId } = useParams();
  const { path } = useRouteMatch();
  const history = useHistory();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loadData, setLoadData] = useState(false);

  const basePath = path.replace(/\/:ownerId(\/edit)?$/, "") || "/admin/car-owners";

  const { owner, loading: ownerLoading, error: ownerError } = useCarOwnerById(
    ownerId,
    loadData
  );
  const { cars, loading: carsLoading } = useOwnerCars(ownerId, loadData);
  const { products } = useOwnerProducts(ownerId, loadData);

  useEffect(() => {
    setLoadData(true);
  }, [ownerId]);

  useEffect(() => {
    if (cars.length > 0) {
      dispatch(setAllCars(cars));
      dispatch(setTableTab2Filer(cars));
    }
  }, [cars, dispatch]);

  useEffect(() => {
    if (products.length > 0) {
      dispatch(setCarOwnerAllProducts(products));
      dispatch(setTableFilterCarOwnerProducts(products));
    }
  }, [products, dispatch]);

  useEffect(() => {
    if (owner) {
      form.setFieldsValue({
        company: owner.company,
        firstname: owner.firstname,
        lastname: owner.lastname,
        street: owner.street,
        city: owner.city,
        zip: owner.zip,
        phone: owner.phone,
        email: owner.email,
        ic: owner.ic,
        dic: owner.dic,
        ic_dph: owner.ic_dph,
        reservation_emails: owner.reservation_emails,
        invoice_emails: owner.invoice_emails,
        invoice_maturity: owner.invoice_maturity,
        bonus: owner.bonus,
        pickup: owner.pickup === true,
      });
    }
  }, [owner, form]);

  const loading = ownerLoading;
  const ownerName = owner?.company || owner?.id || ownerId;

  const listPath = "/admin/car-owners";
  const breadcrumbRoutes = [
    { path: "/admin", breadcrumbName: t("common.home") },
    { path: listPath, breadcrumbName: t("backend.navigation.carOwners") },
    { path: "", breadcrumbName: ownerName },
  ];

  const breadcrumbItemRender = (route, params, routes) => {
    const isLast = routes.indexOf(route) === routes.length - 1;
    if (isLast) {
      return <span>{route.breadcrumbName}</span>;
    }
    return <Link to={route.path}>{route.breadcrumbName}</Link>;
  };

  if (loading && !owner) {
    return (
      <div className="spin">
        <Spin />
      </div>
    );
  }

  if (ownerError && !owner) {
    return (
      <Main>
        <PageHeader ghost title={t("carOwners.list")} />
        <p>{ownerError.message || t("backend.notifications.error.load")}</p>
        <Button type="primary" onClick={() => history.push("/admin/car-owners")}>
          {t("common.back")}
        </Button>
      </Main>
    );
  }

  if (!owner) {
    return null;
  }

  const handleEditOk = () => {}; // view mode: no submit

  return (
    <>
      <PageHeader
        ghost
        title={ownerName}
        routes={breadcrumbRoutes}
        breadcrumbItemRender={breadcrumbItemRender}
        onBack={() => history.push(listPath)}
        extra={
          showEditButton &&
          formReadonly && (
            <Button
              type="primary"
              onClick={() => history.push(`${basePath}/${ownerId}/edit`)}
            >
              <FeatherIcon icon="edit" size={14} />
              &nbsp;{t("common.editRecord")}
            </Button>
          )
        }
      />
      <Main>
        <Tabs>
          <TabPane tab={t("carOwners.modal.tabs.tab1.title")} key="tab1">
            <Tab1
              t={t}
              data={owner}
              formReadonly={formReadonly}
              form={form}
              handleEditOk={handleEditOk}
            />
          </TabPane>
          <TabPane
            tab={t("carOwners.modal.tabs.tab2.title") + " ( " + cars.length + " )"}
            key="tab2"
          >
            <Tab2 t={t} selectedItem={owner} carsData={cars} />
          </TabPane>
          <TabPane tab={t("carOwners.modal.tabs.tab3.title")} key="tab3">
            <Tab3
              t={t}
              selectedItem={owner}
              carsData={cars}
              products={products}
              graphqlVariables={graphqlVariables}
            />
          </TabPane>
        </Tabs>
      </Main>
    </>
  );
};

export default OwnerDetailPage;
